// Real interior-design rules — the kind a senior designer keeps in
// their head. Authored as drop-in presets so a studio can adopt the
// brain with zero rule-writing on day one.
//
// EVERY predicate here is a typed AST against the ontology
// (types/ontology.generated.ts → NodeKind + EdgeKind + PredicateField).
// No string predicates anywhere. Per CLAUDE.md §1 and direct user
// direction: "100% AST grammar driven, no stringy coding".
//
// The natural-language sentence is derived from the AST via
// lib/predicateRender.ts — kept here only as a `natural` field that
// authors can override with a designer-friendly sentence.
//
// Sources informing the rules:
//   • Industry best-practice (AIA Practical Standards, BIFMA spacing,
//     NKBA kitchen ergonomics — paraphrased, not quoted).
//   • Kaggle #7 (vendor SLA), #8 (RERA), #9 (construction PM) for
//     timeline / approval thresholds.
//   • Kaggle #3, #4 (room×style, material×style) for style cohesion.

import type { Predicate } from "../types/rule";

export type RuleCategory =
  | "spatial" | "budget" | "timeline" | "style" | "vendor"
  | "approval" | "accessibility" | "cultural";

export interface DesignerRule {
  id: string;
  category: RuleCategory;
  title: string;
  /** Designer-language sentence — what a senior would say out loud. */
  natural: string;
  /** Typed predicate AST — single source of truth for evaluation. */
  predicate: Predicate;
  trigger: string;
  action: "flag" | "suggest" | "openApproval" | "substitute" | "redistribute";
  severity: "info" | "warn" | "block";
  because: string;
  /** Optional citation back to a dataset that shaped the threshold. */
  dataset?: string;
}

export const CATEGORY_LABEL: Record<RuleCategory, string> = {
  spatial:       "Spatial · ergonomics",
  budget:        "Budget · per-room share",
  timeline:      "Timeline · lead time",
  style:         "Style · cohesion",
  vendor:        "Vendor · SLA & rating",
  approval:      "Approval · ageing & regulatory",
  accessibility: "Accessibility & family fit",
  cultural:      "Cultural · common-sense · vastu",
};

export const CATEGORY_COLOR: Record<RuleCategory, string> = {
  spatial:       "bg-sea/10 text-sea border-sea/30",
  budget:        "bg-fern/10 text-fern border-fern/30",
  timeline:      "bg-foyr/10 text-foyr-deep border-foyr/30",
  style:         "bg-mulberry/10 text-mulberry border-mulberry/30",
  vendor:        "bg-accent/10 text-accent-dark border-accent/30",
  approval:      "bg-ink-950/5 text-ink-950 border-ink-950/20",
  accessibility: "bg-cream-dark text-ink-700 border-border",
  cultural:      "bg-foyr-soft/40 text-foyr-deep border-foyr/30",
};

// ─── Tiny AST helpers — keep the rule literals readable ────────────

const sku = (filterAttr?: string, filterValue?: string) =>
  filterAttr && filterValue
    ? { kind: "pattern" as const, nodeKind: "SKU", filterAttr, filterValue }
    : { kind: "pattern" as const, nodeKind: "SKU" };

const room = (filterAttr?: string, filterValue?: string) =>
  filterAttr && filterValue
    ? { kind: "pattern" as const, nodeKind: "Room", filterAttr, filterValue }
    : { kind: "pattern" as const, nodeKind: "Room" };

const placement = (filterAttr?: string, filterValue?: string) =>
  filterAttr && filterValue
    ? { kind: "pattern" as const, nodeKind: "Placement", filterAttr, filterValue }
    : { kind: "pattern" as const, nodeKind: "Placement" };

const opening = (filterAttr?: string, filterValue?: string) =>
  filterAttr && filterValue
    ? { kind: "pattern" as const, nodeKind: "Opening", filterAttr, filterValue }
    : { kind: "pattern" as const, nodeKind: "Opening" };

const litNum = (value: number, unit?: string) =>
  unit
    ? ({ kind: "lit" as const, type: "number" as const, value, unit })
    : ({ kind: "lit" as const, type: "number" as const, value });

const field = (path: string, type: "number" | "string", unit?: string) =>
  unit
    ? ({ kind: "field" as const, path, type, unit })
    : ({ kind: "field" as const, path, type });

const cmp = (
  lhs: import("../types/rule").ExtendedTerm,
  op: import("../types/rule").Comparator,
  rhs: import("../types/rule").ExtendedTerm,
): Predicate => ({ kind: "cmp", lhs, op, rhs });

const sumOf = (
  over: import("../types/rule").NodePattern,
  fld: import("../types/rule").FieldRef,
  via?: { relation: string; target: import("../types/rule").NodePattern },
): import("../types/rule").AggTerm => via
  ? { kind: "agg", op: "sum", over, field: fld, via }
  : { kind: "agg", op: "sum", over, field: fld };

const avgOf = (
  over: import("../types/rule").NodePattern,
  fld: import("../types/rule").FieldRef,
  via?: { relation: string; target: import("../types/rule").NodePattern },
): import("../types/rule").AggTerm => via
  ? { kind: "agg", op: "avg", over, field: fld, via }
  : { kind: "agg", op: "avg", over, field: fld };

const countOf = (
  over: import("../types/rule").NodePattern,
  opts?: { distinct?: boolean; via?: { relation: string; target: import("../types/rule").NodePattern } },
): import("../types/rule").AggTerm => {
  const t: import("../types/rule").AggTerm = { kind: "agg", op: "count", over };
  if (opts?.distinct) t.distinct = true;
  if (opts?.via) t.via = opts.via;
  return t;
};

const forbidden = (
  subject: import("../types/rule").NodePattern,
  relation: string,
  object: import("../types/rule").NodePattern | import("../types/rule").Literal,
): Predicate => ({ kind: "forbidden", pattern: { subject, relation, object } });

const exists = (
  subject: import("../types/rule").NodePattern,
  relation: string,
  object: import("../types/rule").NodePattern | import("../types/rule").Literal,
): Predicate => ({ kind: "exists", pattern: { subject, relation, object } });

const notInRange = (
  value: import("../types/rule").ExtendedTerm,
  lo: number, hi: number, unit?: string,
): Predicate => unit
  ? { kind: "not_in_range", value, range: { kind: "range", type: "number", lo, hi, unit } }
  : { kind: "not_in_range", value, range: { kind: "range", type: "number", lo, hi } };

const notInSet = (
  value: import("../types/rule").ExtendedTerm,
  values: string[],
): Predicate => ({
  kind: "not_in_set", value,
  set: { kind: "set", type: "string", values },
});

const and = (left: Predicate, right: Predicate): Predicate => ({ kind: "and", left, right });
const or  = (left: Predicate, right: Predicate): Predicate => ({ kind: "or",  left, right });

// ─── DESIGNER_RULES — every predicate is a typed AST ───────────────

export const DESIGNER_RULES: DesignerRule[] = [
  // ─── Spatial · ergonomics ──────────────────────────────────────
  {
    id: "R-sofa-wall",
    category: "spatial",
    title: "Sofa fits the wall",
    natural: "A sofa should be no wider than two-thirds of the wall it sits against.",
    predicate: cmp(field("SKU.widthFt", "number", "ft"), ">", litNum(8, "ft")),
    trigger: "onSkuChanged", action: "flag", severity: "warn",
    because: "Sofas pinned to a wall look squat when they fill > 2/3 of the wall.",
  },
  {
    id: "R-chandelier-clearance",
    category: "spatial",
    title: "Pendant clearance from floor",
    natural: "Pendant lighting hangs no lower than 7'0\" above the finished floor in living areas.",
    predicate: cmp(field("SKU.bottomFt", "number", "ft"), "<", litNum(7.0, "ft")),
    trigger: "onSkuChanged", action: "flag", severity: "warn",
    because: "AIA practical clearance for circulation under hanging fixtures.",
  },
  {
    id: "R-kitchen-triangle",
    category: "spatial",
    title: "Kitchen work-triangle ≤ 26 ft",
    natural: "Sink → fridge → range total path stays under 26 feet in the working kitchen.",
    predicate: cmp(
      sumOf(placement("kind", "cook"), field("Zone.triangleFt", "number", "ft")),
      ">", litNum(26, "ft"),
    ),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NKBA ergonomic baseline — beyond 26 ft, cook flow breaks.",
  },
  {
    id: "R-coffee-clearance",
    category: "spatial",
    title: "Coffee-table to sofa 14–18 in",
    natural: "Leave 14–18 inches between sofa edge and coffee table for legroom.",
    predicate: notInRange(field("Placement.gapIn", "number", "in"), 14, 18, "in"),
    trigger: "onSave", action: "flag", severity: "info",
    because: "Below 14 in cramps legs; above 18 in breaks reach.",
  },
  {
    id: "R-door-swing",
    category: "spatial",
    title: "Door swing ≥ 30 in",
    natural: "No furniture obstructs a door arc by more than 4 inches.",
    predicate: cmp(field("Placement.doorOverlapIn", "number", "in"), ">", litNum(4, "in")),
    trigger: "onSave", action: "flag", severity: "block",
    because: "Blocked doors fail building inspection.",
  },

  // ─── Budget · per-room share ───────────────────────────────────
  {
    id: "R-budget-kitchen-25",
    category: "budget",
    title: "Kitchen ≤ 25% of project budget",
    natural: "Modular kitchen + counter + appliances stays at or under 25% of project.",
    predicate: cmp(
      field("BudgetLine.share", "number", "%"),
      ">", litNum(25, "%"),
    ),
    trigger: "onBudgetChanged", action: "redistribute", severity: "warn",
    because: "Studio average for residential 3BHK; redistribute or expose trade-off.",
    dataset: "Kaggle #9 construction PM",
  },
  {
    id: "R-budget-lighting-8",
    category: "budget",
    title: "Lighting ≤ 8% of total",
    natural: "Lighting (fixtures + drivers + dimmers) lands under 8% of total.",
    predicate: cmp(field("BudgetLine.share", "number", "%"), ">", litNum(8, "%")),
    trigger: "onBudgetChanged", action: "suggest", severity: "info",
    because: "Above 8% usually signals chandelier creep — flag for client review.",
  },
  {
    id: "R-budget-contingency",
    category: "budget",
    title: "Contingency ≥ 10%",
    natural: "Reserve at least 10% contingency on top of approved BOQ.",
    predicate: cmp(field("BudgetLine.share", "number", "%"), "<", litNum(10, "%")),
    trigger: "onBOQRowAdded", action: "flag", severity: "warn",
    because: "Site changes consume 7–12% on average; below 10% is fragile.",
    dataset: "Kaggle #9 construction PM",
  },
  {
    id: "R-budget-vendor-concentration",
    category: "budget",
    title: "Single-vendor concentration ≤ 35%",
    natural: "No single vendor receives more than 35% of project spend.",
    predicate: cmp(
      sumOf({ kind: "pattern", nodeKind: "BudgetLine" }, field("BudgetLine.amount", "number", "₹"),
        { relation: "SUPPLIED_BY", target: { kind: "pattern", nodeKind: "Vendor" } }),
      ">", litNum(35, "%"),
    ),
    trigger: "onBOQRowAdded", action: "flag", severity: "info",
    because: "Diversification protects timeline from any one vendor's slip.",
    dataset: "Kaggle #7 vendor SLA",
  },

  // ─── Timeline · lead time ──────────────────────────────────────
  {
    id: "R-lead-vs-milestone",
    category: "timeline",
    title: "Lead time ≤ milestone − 14 d",
    natural: "Any SKU's lead time finishes at least 14 days before the milestone that needs it.",
    predicate: cmp(field("SKU.leadDays", "number", "days"), ">", litNum(60, "days")),
    trigger: "onSkuChanged", action: "substitute", severity: "warn",
    because: "14 days absorbs typical site delay; below that we risk handover.",
    dataset: "Kaggle #7 vendor SLA",
  },
  {
    id: "R-stone-lead",
    category: "timeline",
    title: "Stone / quartz lead ≥ 4 weeks",
    natural: "Quartz / marble countertops need at least 4 weeks lead time after template.",
    predicate: and(
      cmp(field("SKU.leadDays", "number", "days"), "<", litNum(28, "days")),
      exists(sku("category", "quartz"), "MADE_OF", { kind: "pattern", nodeKind: "Material" }),
    ),
    trigger: "onSkuChanged", action: "flag", severity: "info",
    because: "Templated stone always slips when quoted under 4 weeks.",
  },
  {
    id: "R-millwork-lead",
    category: "timeline",
    title: "Custom millwork ≥ 8 weeks",
    natural: "Custom wardrobes, vanities, and panelling need 8 weeks minimum.",
    predicate: and(
      cmp(field("SKU.leadDays", "number", "days"), "<", litNum(56, "days")),
      exists(sku("category", "wardrobe"), "IN_ROOM", room()),
    ),
    trigger: "onSkuChanged", action: "flag", severity: "warn",
    because: "Below 8 weeks signals an under-promised vendor — verify before BOQ.",
  },
  {
    id: "R-civil-before-finish",
    category: "timeline",
    title: "Civil work ends 3 weeks before install",
    natural: "Wet civil work must complete at least 3 weeks before any finish install.",
    predicate: cmp(field("Milestone.slackDays", "number", "days"), "<", litNum(21, "days")),
    trigger: "onMilestoneSet", action: "flag", severity: "warn",
    because: "Moisture damage to flooring / millwork from rushed civil hand-off.",
    dataset: "Kaggle #9 construction PM",
  },

  // ─── Style · cohesion ──────────────────────────────────────────
  {
    id: "R-room-cohesion",
    category: "style",
    title: "Per-room style cohesion ≥ 0.78",
    natural: "Average cosine of placements vs project Intent stays ≥ 0.78 per room.",
    predicate: cmp(
      avgOf(placement(), field("SKU.styleMatch", "number", "%"),
        { relation: "IN_ROOM", target: room() }),
      "<", litNum(0.78, "%"),
    ),
    trigger: "onSave", action: "suggest", severity: "warn",
    because: "Below 0.78 reads as mismatched; restyle suggestions before render spend.",
    dataset: "Kaggle #3 galinakg",
  },
  {
    id: "R-finish-count",
    category: "style",
    title: "≤ 3 finishes per room",
    natural: "Cap material finishes (excluding metals) at 3 per room.",
    predicate: cmp(
      countOf({ kind: "pattern", nodeKind: "Material" }, { distinct: true,
        via: { relation: "IN_ROOM", target: room() } }),
      ">", litNum(3),
    ),
    trigger: "onSave", action: "flag", severity: "info",
    because: "More than 3 finishes muddies hierarchy in residential rooms.",
    dataset: "Kaggle #4 material/style",
  },
  {
    id: "R-pattern-cap",
    category: "style",
    title: "≤ 2 patterns per room",
    natural: "No more than 2 distinct patterns (textile + wallpaper) per room.",
    predicate: cmp(
      countOf(placement("kind", "Pattern"), { distinct: true,
        via: { relation: "IN_ROOM", target: room() } }),
      ">", litNum(2),
    ),
    trigger: "onSave", action: "flag", severity: "info",
    because: "Pattern stacking fatigues the eye; client rejection risk.",
  },
  {
    id: "R-60-30-10",
    category: "style",
    title: "Colour split 60-30-10",
    natural: "Colour split holds 60% dominant / 30% secondary / 10% accent (±5pp).",
    predicate: notInRange(field("Style.paletteShare", "number", "%"), 0.55, 0.65, "%"),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "Classic editorial palette — deviation reads as accidental.",
  },

  // ─── Vendor · SLA & rating ─────────────────────────────────────
  {
    id: "R-vendor-rating",
    category: "vendor",
    title: "Vendor rating ≥ 4.0",
    natural: "Vendors below 4.0 don't ship into premium projects without sign-off.",
    predicate: cmp(field("Vendor.rating", "number", "/5"), "<", litNum(4.0, "/5")),
    trigger: "onSkuChanged", action: "openApproval", severity: "warn",
    because: "Track-record below 4.0 historically correlates with re-work.",
    dataset: "Kaggle #7 vendor SLA",
  },
  {
    id: "R-vendor-on-time",
    category: "vendor",
    title: "On-time rate ≥ 85%",
    natural: "Vendor on-time rate ≥ 85% required for projects handing over in < 90 days.",
    predicate: cmp(field("Vendor.onTimeRate", "number", "%"), "<", litNum(85, "%")),
    trigger: "onSkuChanged", action: "substitute", severity: "warn",
    because: "Tight handovers amplify any vendor slip; pre-empt now.",
    dataset: "Kaggle #7 vendor SLA",
  },

  // ─── Approval · ageing & regulatory ────────────────────────────
  {
    id: "R-approval-age",
    category: "approval",
    title: "Approval > 5 days → escalate",
    natural: "Any pending Approval older than 5 days escalates to the PM.",
    predicate: cmp(field("Approval.ageDays", "number", "days"), ">", litNum(5, "days")),
    trigger: "onApprovalDecided", action: "openApproval", severity: "warn",
    because: "Past 5 days, the client has cooled; PM needs to step in.",
  },
  {
    id: "R-rera-required",
    category: "approval",
    title: "RERA filing required > ₹50 L",
    natural: "Projects over ₹50 L in regulated states need a RERA approval node before handover.",
    predicate: and(
      cmp(field("Project.budgetCap", "number", "₹"), ">", litNum(5_000_000, "₹")),
      // ¬ ∃ Approval(kind=RERA) for the project → "absent" represented as forbidden exists
      forbidden({ kind: "pattern", nodeKind: "Approval", filterAttr: "kind", filterValue: "RERA" },
        "OF", { kind: "pattern", nodeKind: "Project" }),
    ),
    trigger: "onSave", action: "openApproval", severity: "block",
    because: "Regulatory; auto-template the packet so the team isn't blocked.",
    dataset: "Kaggle #8 Gujarat RERA",
  },
  {
    id: "R-postsign-change",
    category: "approval",
    title: "Material change after sign-off",
    natural: "Any colour/material change after client sign-off requires a new Approval.",
    predicate: exists(placement(), "MATERIAL_CHANGED_AFTER",
      { kind: "pattern", nodeKind: "Approval", filterAttr: "state", filterValue: "approved" }),
    trigger: "onSkuChanged", action: "openApproval", severity: "warn",
    because: "Prevent silent drift between approved boards and built reality.",
  },

  // ─── Accessibility & family fit ────────────────────────────────
  {
    id: "R-senior-grabbar",
    category: "accessibility",
    title: "Senior client → bathroom grab-bar",
    natural: "If any client persona is 'senior', every bathroom adds a grab-bar Placement.",
    predicate: and(
      exists({ kind: "pattern", nodeKind: "Client", filterAttr: "persona", filterValue: "senior" },
        "OWNS", { kind: "pattern", nodeKind: "Project" }),
      forbidden(placement("kind", "GrabBar"), "IN_ROOM", room("roomKind", "bath")),
    ),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "Mobility safety; cheap to add early, retrofit is expensive.",
  },
  {
    id: "R-kids-safety",
    category: "accessibility",
    title: "Kids → rounded furniture corners",
    natural: "With children under 8, prefer rounded corners and no low glass surfaces.",
    predicate: and(
      cmp(field("Client.childrenUnder8", "number"), ">", litNum(0)),
      exists(placement("cornerKind", "sharp"), "IN_ROOM", room()),
    ),
    trigger: "onSkuChanged", action: "suggest", severity: "info",
    because: "Lowers paediatric ER cases; expected by parents.",
  },
  {
    id: "R-pet-friendly",
    category: "accessibility",
    title: "Pets → washable upholstery, hard floors",
    natural: "Pet households prefer washable upholstery and avoid plush carpeting in living areas.",
    predicate: and(
      cmp(field("Client.pets", "number"), ">", litNum(0)),
      exists(placement(), "MADE_OF",
        { kind: "pattern", nodeKind: "Material", filterAttr: "kind", filterValue: "plushWool" }),
    ),
    trigger: "onSkuChanged", action: "suggest", severity: "info",
    because: "Hygiene + claw durability — drives long-term satisfaction.",
  },

  // ─── Cultural · common-sense · vastu (Indian / Subcontinental) ──
  // Every Indian designer knows these. None are in any AIA / NKBA
  // handbook. They surface as "oh-no" moments after the family
  // visits the site. The brain catches them on save.
  {
    id: "R-bath-faces-pooja",
    category: "cultural",
    title: "No bathroom door faces the pooja room",
    natural: "A bathroom door cannot open directly toward the pooja / temple room.",
    predicate: forbidden(room("roomKind", "bath"), "doorFacesRoom", room("roomKind", "pooja")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "Culturally unacceptable in most Indian households — caught now, not at site visit.",
  },
  {
    id: "R-kitchen-not-below-bath",
    category: "cultural",
    title: "Kitchen never directly under a bathroom",
    natural: "Avoid placing the kitchen directly below an upper-floor bathroom.",
    predicate: forbidden(room("roomKind", "kitchen"), "below", room("roomKind", "bath")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "Plumbing / water-seepage risk plus vastu objection from many Indian clients.",
  },
  {
    id: "R-stove-vs-sink",
    category: "cultural",
    title: "Stove and sink not in direct opposition",
    natural: "Cook-top and sink should not sit on the exact opposite line within 2 ft.",
    predicate: forbidden(placement("kind", "Stove"), "directlyOppositeOf", placement("kind", "Sink")),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "Fire–water opposition; practical splash issue and vastu concern.",
  },
  {
    id: "R-bed-foot-to-bathdoor",
    category: "cultural",
    title: "Bed footboard does not point at the bathroom door",
    natural: "The bed should not be oriented so the feet point straight at the bathroom door.",
    predicate: forbidden(placement("kind", "Bed"), "footFaces", opening("kind", "BathroomDoor")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "Energy / hygiene perception in many South Asian households.",
  },
  {
    id: "R-mirror-vs-bed",
    category: "cultural",
    title: "Mirror not directly facing the bed",
    natural: "A wardrobe / dresser mirror should not be directly across the bed line.",
    predicate: forbidden(placement("kind", "Mirror"), "facingCenterlineOf", placement("kind", "Bed")),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "Sleep-quality concern and feng-shui / vastu objection.",
  },
  {
    id: "R-entrance-vs-stairs",
    category: "cultural",
    title: "Main entrance does not face descending stairs",
    natural: "The main door should not open onto a staircase going down (within 8 ft).",
    predicate: forbidden(opening("kind", "MainDoor"), "faces",
      { kind: "pattern", nodeKind: "Stair", filterAttr: "direction", filterValue: "down" }),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "Long-standing convention — and a real safety issue with toddlers / elders.",
  },
  {
    id: "R-pooja-faces-east",
    category: "cultural",
    title: "Pooja altar faces East or North-East",
    natural: "The pooja / mandir altar wall should be on the East or North-East orientation.",
    predicate: notInSet(field("Placement.facing", "string"), ["E", "NE"]),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "Standard request from most Indian families; cheap to honour at the planning stage.",
  },
  {
    id: "R-master-room-orientation",
    category: "cultural",
    title: "Master bedroom prefers SW orientation",
    natural: "Place the master bedroom in the South-West quadrant of the plan when possible.",
    predicate: notInSet(field("Room.quadrant", "string"), ["SW"]),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "Vastu preference still cited in 70%+ of premium residential briefs in India.",
  },
  {
    id: "R-beam-over-bed",
    category: "cultural",
    title: "No structural beam runs over a bed",
    natural: "Avoid placing a bed directly beneath an exposed structural beam.",
    predicate: forbidden(placement("kind", "Bed"), "below",
      { kind: "pattern", nodeKind: "Element", filterAttr: "kind", filterValue: "Beam" }),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "Sleep-quality + cultural belief; suggest a false-ceiling soffit if unavoidable.",
  },
  {
    id: "R-dining-vs-maindoor",
    category: "cultural",
    title: "Dining table not directly in line with the main door",
    natural: "The dining table shouldn't sit on the direct sight-line from the main door.",
    predicate: forbidden(placement("kind", "DiningTable"), "onAxisOf", opening("kind", "MainDoor")),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "Privacy + the older 'food-fortune' superstition; trivially fixable on the floor plan.",
  },

  /* ─── PROFESSIONAL / STATUTORY RULES ──────────────────────────────
   * Spec 07. Each rule is grounded in a real published standard.
   * Sub-clause numbers are deliberately NOT fabricated; citations go
   * to the part / section level the standard publishes at. See
   * docs/architectural-rules-sources.md for the full reference list
   * and jurisdiction notes.
   *
   * The thresholds are published values for residential occupancy
   * (NBC 2016 Group A). State by-laws (Maharashtra DCPR, Karnataka
   * KCBR, etc.) may impose stricter minimums — the rule's `because`
   * field flags where this matters.
   */

  {
    id: "R-nbc-habitable-room-min",
    category: "spatial",
    title: "Habitable room ≥ 9.5 m²",
    natural: "Every habitable room (bedroom / living) keeps a floor area of at least 9.5 square metres.",
    predicate: cmp(field("Room.areaSqM", "number", "m²"), "<", litNum(9.5, "m²")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 3 sets the residential habitable-room minimum. State by-laws may be stricter.",
    dataset: "NBC 2016 Part 3 — Development Control & General Building Requirements",
  },
  {
    id: "R-nbc-ceiling-height-min",
    category: "spatial",
    title: "Clear ceiling height ≥ 2.75 m",
    natural: "Habitable rooms have a clear floor-to-ceiling height of at least 2.75 metres.",
    predicate: cmp(field("Room.ceilingHt", "number", "m"), "<", litNum(2.75, "m")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 3 — residential clear height minimum. False ceilings reduce the clear dimension.",
    dataset: "NBC 2016 Part 3",
  },
  {
    id: "R-nbc-kitchen-min-area",
    category: "spatial",
    title: "Kitchen ≥ 5 m²",
    natural: "Standalone kitchens have at least 5 m²; kitchen-cum-dining at least 7.5 m².",
    predicate: cmp(field("Room.areaSqM", "number", "m²"), "<", litNum(5, "m²")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 3 — kitchen minimum (Group A residential). Below 5 m² triggers a redesign nudge before render.",
    dataset: "NBC 2016 Part 3",
  },
  {
    id: "R-nbc-bath-min-area",
    category: "spatial",
    title: "Bath ≥ 1.8 m² with min 1.2 m dimension",
    natural: "A bath has at least 1.8 m² area and a shortest internal clear dimension of 1.2 m.",
    predicate: cmp(field("Room.areaSqM", "number", "m²"), "<", litNum(1.8, "m²")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 3 — bath minimum. Pair with the min-dimension check for accessibility readiness.",
    dataset: "NBC 2016 Part 3",
  },
  {
    id: "R-nbc-wc-min-area",
    category: "spatial",
    title: "WC ≥ 1.1 m²",
    natural: "A water closet (WC) cubicle has a floor area of at least 1.1 m².",
    predicate: cmp(field("Room.areaSqM", "number", "m²"), "<", litNum(1.1, "m²")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 3 — WC cubicle minimum. Common violation in micro-flats.",
    dataset: "NBC 2016 Part 3",
  },
  {
    id: "R-nbc-egress-stair-width",
    category: "accessibility",
    title: "Egress staircase ≥ 1.0 m wide",
    natural: "Every egress staircase serving residential use is at least 1.0 metre clear width.",
    predicate: cmp(field("FloorPlan.staircaseMinWidthM", "number", "m"), "<", litNum(1.0, "m")),
    trigger: "onSave", action: "flag", severity: "block",
    because: "NBC 2016 Part 4 — residential egress minimum. Blocks render until corrected; this is life-safety.",
    dataset: "NBC 2016 Part 4 — Fire & Life Safety",
  },
  {
    id: "R-nbc-egress-door-clear",
    category: "accessibility",
    title: "Egress door clear width ≥ 0.9 m",
    natural: "Egress doors give at least 0.9 m clear width — measured between the door face and the frame stop.",
    predicate: cmp(field("FloorPlan.doorClearWidthM", "number", "m"), "<", litNum(0.9, "m")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 4 — residential egress door minimum. ANSI A117.1 / Indian universal-design guidance recommends 0.815 m for wheelchair.",
    dataset: "NBC 2016 Part 4 + ANSI A117.1",
  },
  {
    id: "R-nbc-corridor-min-width",
    category: "accessibility",
    title: "Corridor ≥ 1.0 m wide",
    natural: "Common corridors serving more than one room are at least 1.0 m clear width.",
    predicate: cmp(field("FloorPlan.corridorMinWidthM", "number", "m"), "<", litNum(1.0, "m")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NBC 2016 Part 4 — corridor minimum, residential.",
    dataset: "NBC 2016 Part 4",
  },
  {
    id: "R-nbc-highrise-two-stairs",
    category: "accessibility",
    title: "High-rise (> 15 m) needs ≥ 2 staircases",
    natural: "Buildings taller than 15 m have at least two staircases for egress redundancy.",
    predicate: and(
      cmp(field("FloorPlan.heightM", "number", "m"), ">", litNum(15, "m")),
      cmp(field("FloorPlan.staircaseCount", "number"), "<", litNum(2)),
    ),
    trigger: "onSave", action: "flag", severity: "block",
    because: "NBC 2016 Part 4 — high-rise egress redundancy. State fire NOC won't issue without this.",
    dataset: "NBC 2016 Part 4",
  },
  {
    id: "R-ecbc-envelope-shgc",
    category: "style",
    title: "Envelope SHGC ≤ 0.27 (cool climates)",
    natural: "In composite / hot-dry / warm-humid climates, the building envelope's solar heat-gain coefficient stays ≤ 0.27.",
    predicate: cmp(field("FloorPlan.shgc", "number"), ">", litNum(0.27)),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "ECBC 2017 envelope target for non-cold climate zones. Drives glazing / shading choices.",
    dataset: "ECBC 2017 — Energy Conservation Building Code",
  },
  {
    id: "R-ecbc-lpd-residential",
    category: "style",
    title: "Lighting Power Density ≤ 8 W/m² (residential)",
    natural: "Connected lighting load stays at or below 8 W per square metre for residential areas.",
    predicate: cmp(field("FloorPlan.lpdWperSqM", "number", "W/m²"), ">", litNum(8, "W/m²")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "ECBC 2017 Table 8 — residential LPD ceiling. Drives LED count / fixture choice on the RCP.",
    dataset: "ECBC 2017 Table 8",
  },
  {
    id: "R-rera-registration-threshold",
    category: "approval",
    title: "RERA registration if > 500 m² OR > 8 apartments",
    natural: "Projects above 500 m² total area or with more than 8 saleable units must register under the state RERA authority.",
    predicate: and(
      or(
        cmp(field("Project.totalAreaSqM", "number", "m²"), ">", litNum(500, "m²")),
        cmp(field("Project.unitCount", "number"), ">", litNum(8)),
      ),
      cmp(field("Project.reraRegistered", "string"), "==", { kind: "lit", type: "string", value: "no" }),
    ),
    trigger: "onSave", action: "openApproval", severity: "block",
    because: "RERA 2016 §3 — registration threshold (national). State RERAs may set lower thresholds.",
    dataset: "RERA Act 2016 §3 — Real Estate Regulation Act",
  },
  {
    id: "R-igbc-rainwater-harvest",
    category: "approval",
    title: "Rainwater harvesting cover ≥ 60 % of roof area",
    natural: "Designs targeting IGBC Green Homes / GRIHA capture at least 60 % of roof runoff into a harvesting system.",
    predicate: cmp(field("FloorPlan.rainwaterCoverPct", "number", "%"), "<", litNum(60, "%")),
    trigger: "onSave", action: "suggest", severity: "info",
    because: "IGBC Green Homes / GRIHA mandatory credit for water efficiency. State permits often require it independently.",
    dataset: "IGBC Green Homes + GRIHA Manual",
  },
  {
    id: "R-ansi-wheelchair-turning",
    category: "accessibility",
    title: "Wheelchair turning radius 1.5 m clear",
    natural: "Any room intended for universal access keeps a 1.5 m diameter clear floor for wheelchair turning.",
    predicate: cmp(field("Room.minDimM", "number", "m"), "<", litNum(1.5, "m")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "ANSI A117.1 turning-radius standard; NBC 2016 Part 3 § Universal Design also references it.",
    dataset: "ANSI A117.1 + NBC 2016 Part 3",
  },
  {
    id: "R-nkba-kitchen-triangle",
    category: "spatial",
    title: "Kitchen work-triangle ≤ 8 m total",
    natural: "Sink → fridge → range — the three legs of the kitchen work-triangle sum to no more than 8 m.",
    predicate: cmp(field("Room.workTriangleM", "number", "m"), ">", litNum(8, "m")),
    trigger: "onSave", action: "flag", severity: "warn",
    because: "NKBA ergonomic baseline. Beyond 8 m, cook-flow breaks (well-published in NKBA Planning Guidelines).",
    dataset: "NKBA Kitchen Planning Guidelines",
  },
];
